import {
  Handshake,
  Home,
  ListTodo,
  Radar,
  type LucideIcon,
} from "lucide-react";

import { MARKET_BASE } from "@/components/market-intel/routes";

export interface MobileTab {
  href: string;
  label: string;
  Icon: LucideIcon;
}

/** The mobile bottom bar's four tabs, left to right. */
export const MOBILE_TABS: readonly MobileTab[] = [
  { href: "/", label: "crm.mobile.nav_home", Icon: Home },
  { href: "/tasks", label: "crm.mobile.nav_tasks", Icon: ListTodo },
  { href: "/deals", label: "crm.mobile.nav_deals", Icon: Handshake },
  // The section index redirects to its daily report (MarketIntelPage).
  { href: MARKET_BASE, label: "crm.mobile.nav_market", Icon: Radar },
];

const underPath = (prefix: string, pathname: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/**
 * Pages owned by a tab besides its own subtree: the closest tab lights up so
 * the bar always says where the user is.
 *
 * - Tareas: the team task board (/team_tasks, its create / edit forms).
 * - Oportunidades: a deal opened from a report or the agenda
 *   (/informes/:id renders the mobile deal detail). The reports list itself
 *   (/informes) is a Más module and keeps no pill.
 *
 * Intentionally NO tab (the pill fades out): contacts and companies (list
 * and detail), Más, the calendar, messages, reports, settings and every
 * other Más module. They are reached from Más or the + menu, not from a tab,
 * and lighting an unrelated tab would lie about where the user is.
 */
const EXTRA_ROUTES: readonly {
  tab: string;
  matches: (path: string) => boolean;
}[] = [
  { tab: "/tasks", matches: (path) => underPath("/team_tasks", path) },
  { tab: "/deals", matches: (path) => /^\/informes\/[^/]+/.test(path) },
];

const matchesTab = (href: string, pathname: string): boolean =>
  href === "/"
    ? pathname === "/"
    : underPath(href, pathname) ||
      EXTRA_ROUTES.some(
        (route) => route.tab === href && route.matches(pathname),
      );

/**
 * Index of the tab owning `pathname` (its own page and everything below it,
 * /deals/12/show is the deals tab, plus EXTRA_ROUTES), or -1 when no tab
 * does (/contacts, /more, /calendar...): the pill then fades out.
 */
export const activeTabIndex = (
  pathname: string,
  tabs: readonly MobileTab[] = MOBILE_TABS,
): number => tabs.findIndex((tab) => matchesTab(tab.href, pathname));
